import type { ErrorBounds } from "src/components/graphs/cross-audience-graph/types";
import type {
  AllReportSchedulesQuery,
  MinimalDecisionEngineFlowMessagesQuery,
} from "src/graphql";
import * as yup from "yup";
import type { DecisionEngine } from "..";
import type {
  CorrelationChart,
  FilterColumnType,
  FilterValue,
} from "./correlation-breakdowns/types";
import type { GlobalBreakdownChart } from "./global-breakdowns/types";
import type { RouteTab } from "src/components/route-tabs";

export type InsightsContext = {
  agent: MinimalAgent;
  outcomes: MinimalOutcome[];
  flowMessages:
    | MinimalDecisionEngineFlowMessagesQuery["decision_engine_flow_messages"]
    | undefined;
  activeTab: RouteTab | undefined;
  reportSchedules: AllReportSchedulesQuery["report_schedule"] | undefined;
  isDemo: boolean;
  /**
   * Resolves a breakdown column name to its human-readable label, preferring
   * the insightsDisplayName from the user feature schema and falling back to
   * a derived label for columns not in the schema (action features, tag
   * features, native decision engine columns).
   */
  getColumnDisplayName: (column: string) => string;
};

export enum InsightsChartType {
  GlobalBreakdown = "global",
  CorrelationsBreakdown = "correlation",
  Heatmap = "heatmap",
}

export enum InsightsType {
  Summary = "summary",
  UserBreakdowns = "user-breakdowns",
  PersonalizationInsights = "personalization",
  CreativeOverview = "creative-overview",
  Timing = "timing",
  Affinities = "affinities",
  SendFidelity = "send-fidelity",
  SendComparison = "send-comparison",
  PerformanceMatrix = "performance-matrix",
  ContentRecommendations = "content-recommendations",
  Debug = "debug",
}

export enum BreakdownType {
  UserFeature = "user_feature",
  ActionFeature = "action_feature",
}

export type BreakdownBy = {
  column: string;
  type: BreakdownType;
};

export type ChartFilter = {
  value: FilterValue;
  type: FilterColumnType;
  column: string;
  valueLabel: string; // label for the raw filter value (e.g. "{ min: 1, max: 10 }" -> "1-10")
};

export enum BreakdownTableType {
  Correlations = "correlations",
  FeatureBreakdowns = "feature_breakdowns",
}

export type InsightsChart = GlobalBreakdownChart | CorrelationChart;

export type CohortData = {
  color: string;
  value: number | undefined;
  label: string;
  breakdownValue: string | null;
  errorBounds?: ErrorBounds;
  metadata?: { numerator: number; denominator: number };
  crediblyDifferent?: boolean;
};

export type BaselineData = Omit<CohortData, "errorBounds" | "breakdownValue">;

export type CorrelationsBreakdownData = {
  label: string;
  baseline: BaselineData;
  cohort: CohortData;
  lowData?: boolean;
  isSummaryRow?: boolean;
  rankScore?: number;
};

export type FeatureBreakdownData = {
  label: string;
  cohort: CohortData & { errorBounds?: undefined };
  lowData?: boolean;
};

export type MinimalAgent = Pick<
  DecisionEngine["flows"][0],
  "id" | "name" | "config"
> & {
  outcomes: Array<{
    outcome: MinimalOutcome;
  }>;
};

export type MinimalOutcome = Pick<
  DecisionEngine["flows"][0]["outcomes"][0]["outcome"],
  "id" | "name" | "weight"
>;

/**
 * Since the charts are generated by the backend but the main request object is
 * stored as a JSON blob, we should validate the request schema here to ensure
 * the data is coming in as expected. This schema can also be used to catch any
 * missing links between the frontend and backend.
 */
export const chartSchema = yup.object().shape({
  id: yup.string().required(),
  outcome_id: yup.string().required(),
  chart_type: yup
    .string()
    .oneOf([
      InsightsChartType.GlobalBreakdown,
      InsightsChartType.CorrelationsBreakdown,
      InsightsChartType.Heatmap,
    ])
    .required(),
  breakdown_by_type: yup
    .string()
    .oneOf([BreakdownType.UserFeature, BreakdownType.ActionFeature])
    .required(),
  breakdown_by_dimension: yup.string().required(),

  // Filter is for specifying the subsegment for comparing against the baseline/total population
  filter_by: yup.string().when("chart_type", {
    is: InsightsChartType.CorrelationsBreakdown,
    then: yup.string().required(),
    otherwise: yup.string().nullable(),
  }),
  filter_value: yup.string().when("chart_type", {
    is: InsightsChartType.CorrelationsBreakdown,
    then: yup.string().required(),
    otherwise: yup.string().nullable(),
  }),

  rank: yup.number().required(),
  top_chart: yup.boolean().optional(),
  time_series_request: yup.object().required(),
});
